import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Motion";
import { Seo } from "../components/Seo";
import { ARTICLES } from "../lib/data";

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/insights" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <article>
      <Seo title={`${article.title} — Ethikally`} description={article.excerpt} image={article.image} />

      {/* Hero */}
      <section className="pt-36 md:pt-44 relative z-10" data-testid="article-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/insights" className="inline-flex items-center gap-2 font-mono text-xs tracking-wider uppercase text-navy/50 hover:text-forest transition-colors" data-testid="article-back">
            <ArrowLeft size={16} /> All insights
          </Link>
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-forest mt-8">
            <span>{article.category}</span><span className="text-navy/30">/</span><span className="text-navy/40">{article.date}</span>
            <span className="text-navy/30">/</span><span className="text-navy/40">{article.readTime}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 font-medium">
            {article.title}
          </h1>
          <p className="text-navy/60 text-lg leading-relaxed mt-6">{article.excerpt}</p>
          <p className="font-mono text-xs tracking-wider uppercase text-navy/40 mt-6">By {article.author}</p>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-5xl mx-auto px-6 md:px-12 mt-12 relative z-10"
      >
        <div className="overflow-hidden clip-frame h-72 md:h-[30rem]">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </motion.div>

      {/* Body */}
      <section className="py-16 md:py-20 relative z-10">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {article.body.map((para, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <p className={`text-navy/80 leading-[1.8] ${i === 0 ? "text-xl md:text-2xl font-heading text-navy" : "text-lg"} mt-7 first:mt-0`}>
                {para}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="pb-28 relative z-10 bg-surface pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-12">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link to={`/insights/${a.slug}`} className="group block" data-testid={`article-related-${a.slug}`}>
                  <div className="overflow-hidden clip-frame h-44">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="font-mono text-xs uppercase tracking-widest text-forest mt-4">{a.category}</div>
                  <h3 className="font-heading text-xl mt-2 tracking-tight group-hover:text-forest transition-colors flex items-start gap-1">
                    {a.title}
                  </h3>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-14">
            <Link to="/contact" className="group inline-flex items-center gap-3 rounded-full bg-navy text-cream px-8 py-4 font-medium hover:bg-forest transition-colors" data-testid="article-cta-contact">
              Work with our team <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
